import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Langs from "../../../../app/lang/langs";
import {
    setAddMembersModalVisibility,
    addRemoveProjectCollabsManagers,
    setAddMembersMode,
} from "../../vcompSlice";

import {
    Dialog,
    Slide,
    AppBar,
    Toolbar,
    IconButton,
    Typography,
    Box,
    Button,
    Autocomplete,
    TextField,
    Chip,
    Badge,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import { keyframes } from "@emotion/react";

/* ── Color tokens ──────────────────────────────────────────────────── */
const ORANGE = "#FF9015";
const ORANGE_LIGHT = "rgba(255, 144, 21, 0.10)";
const ORANGE_GLOW = "rgba(255, 144, 21, 0.35)";
const ORANGE_DARK = "#e07400";

/* ── Keyframe animations ───────────────────────────────────────────── */

const shimmer = keyframes`
    0%   { background-position: -200% center; }
    100% { background-position: 200% center; }
`;

const fadeSlideUp = keyframes`
    0%   { opacity: 0; transform: translateY(12px); }
    100% { opacity: 1; transform: translateY(0); }
`;

const fadeIn = keyframes`
    0%   { opacity: 0; }
    100% { opacity: 1; }
`;

const emptyBounce = keyframes`
    0%   { opacity: 0; transform: translateY(16px) scale(0.92); }
    60%  { opacity: 1; transform: translateY(-2px) scale(1.02); }
    100% { opacity: 1; transform: translateY(0) scale(1); }
`;

const badgePop = keyframes`
    0%   { transform: scale(0); }
    60%  { transform: scale(1.25); }
    100% { transform: scale(1); }
`;

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

export default function MobileAddMembersModal() {
    const dispatch = useDispatch();

    const visible = useSelector((state) => state.vcomp.addMembersModalVisibility);
    const collabsNames = useSelector((state) => state.vcomp.collabsNames);
    const collabsIds = useSelector((state) => state.vcomp.projectCollabs);
    const managersIds = useSelector((state) => state.vcomp.projectManagers);
    const onCreateProject = useSelector((state) => state.vcomp.onCreateProject);
    const mode = useSelector((state) => state.vcomp.addMembersMode);

    const [associatedMembers, setAssociatedMembers] = useState([]);

    const collabsList = useMemo(() => {
        return collabsNames
            .filter((collab) => !collabsIds.includes(collab.id))
            .map(({ id, nom, prenom, team_name }) => ({
                id,
                fullName: `${nom} ${prenom}`,
                compoundName: `${nom} ${prenom} ${team_name}`,
                teamName: team_name || "",
            }));
    }, [collabsNames, collabsIds]);

    const managersList = useMemo(() => {
        return collabsNames
            .filter((collab) => !managersIds.includes(collab.id))
            .map(({ id, nom, prenom, team_name }) => ({
                id,
                fullName: `${nom} ${prenom}`,
                compoundName: `${nom} ${prenom} ${team_name}`,
                teamName: team_name || "",
            }));
    }, [collabsNames, managersIds]);

    const options = mode ? managersList : collabsList;

    const handleClose = () => {
        dispatch(setAddMembersModalVisibility(false));
        dispatch(setAddMembersMode(null));
        setAssociatedMembers([]);
    };

    const handleAdd = () => {
        const addedCollabsIds = associatedMembers.map((m) => m.id);
        if (!mode) {
            dispatch(
                addRemoveProjectCollabsManagers({
                    id_project: onCreateProject.id,
                    added_collabs: addedCollabsIds,
                })
            );
        } else {
            dispatch(
                addRemoveProjectCollabsManagers({
                    id_project: onCreateProject.id,
                    added_managers: addedCollabsIds,
                })
            );
        }
        handleClose();
    };

    // Reset selection when modal opens
    useEffect(() => {
        if (visible) {
            setAssociatedMembers([]);
        }
    }, [visible]);

    return (
        <Dialog
            open={visible || false}
            onClose={handleClose}
            TransitionComponent={Transition}
            transitionDuration={{ enter: 420, exit: 300 }}
            fullWidth
            maxWidth="sm"
            sx={{
                zIndex: 1500,
                "& .MuiBackdrop-root": {
                    backdropFilter: "blur(10px) saturate(1.4)",
                    WebkitBackdropFilter: "blur(10px) saturate(1.4)",
                    backgroundColor: "rgba(0, 0, 0, 0.3)",
                    transition: "all 400ms cubic-bezier(0.4, 0, 0.2, 1)",
                },
                "& .MuiDialog-paper": {
                    borderRadius: "20px 20px 0 0",
                    position: "fixed",
                    bottom: 0,
                    m: 0,
                    maxHeight: "85vh",
                    width: "100%",
                    overflow: "hidden",
                    background:
                        "linear-gradient(168deg, rgba(255,255,255,0.97) 0%, rgba(255,255,255,0.93) 100%)",
                    backdropFilter: "blur(20px) saturate(1.8)",
                    WebkitBackdropFilter: "blur(20px) saturate(1.8)",
                    border: "1px solid rgba(255, 144, 21, 0.08)",
                    borderBottom: "none",
                    boxShadow: [
                        "0 -2px 8px rgba(255, 144, 21, 0.05)",
                        "0 -8px 32px rgba(255, 144, 21, 0.08)",
                        "0 -24px 48px rgba(0, 0, 0, 0.10)",
                    ].join(", "),
                    /* ── Shimmer accent bar at top ── */
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "3px",
                        background: `linear-gradient(90deg, ${ORANGE}, #FFC100, ${ORANGE})`,
                        backgroundSize: "200% auto",
                        animation: `${shimmer} 3s ease-in-out infinite`,
                        borderRadius: "20px 20px 0 0",
                        zIndex: 1,
                    },
                },
            }}
        >
            {/* ── Sticky Header ────────────────────────────────────────── */}
            <AppBar
                position="sticky"
                elevation={0}
                sx={{
                    bgcolor: "rgba(255, 255, 255, 0.85)",
                    backdropFilter: "blur(16px) saturate(1.6)",
                    WebkitBackdropFilter: "blur(16px) saturate(1.6)",
                    borderBottom: "1px solid rgba(255, 144, 21, 0.08)",
                    borderRadius: "20px 20px 0 0",
                }}
            >
                <Toolbar sx={{ justifyContent: "space-between", minHeight: 60 }}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            animation: `${fadeSlideUp} 450ms cubic-bezier(0, 0, 0.2, 1) 100ms both`,
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 36,
                                height: 36,
                                borderRadius: "10px",
                                background: ORANGE_LIGHT,
                                position: "relative",
                                "&::after": {
                                    content: '""',
                                    position: "absolute",
                                    inset: -2,
                                    borderRadius: "12px",
                                    background: `radial-gradient(circle, ${ORANGE_GLOW} 0%, transparent 70%)`,
                                    opacity: 0.3,
                                    pointerEvents: "none",
                                },
                            }}
                        >
                            <GroupAddIcon
                                sx={{
                                    color: ORANGE,
                                    fontSize: 20,
                                    filter: `drop-shadow(0 1px 2px ${ORANGE_GLOW})`,
                                }}
                            />
                        </Box>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: 700,
                                color: "#111318",
                                letterSpacing: "-0.02em",
                                fontSize: "1.05rem",
                            }}
                        >
                            {mode ? (
                                <Langs str="ADD_MANAGERS" />
                            ) : (
                                <Langs str="ADD_MEMBERS" />
                            )}
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={handleClose}
                        sx={{
                            color: "#4b5263",
                            minWidth: 44,
                            minHeight: 44,
                            borderRadius: "12px",
                            background: "rgba(0, 0, 0, 0.03)",
                            transition: "all 250ms cubic-bezier(0.4, 0, 0.2, 1)",
                            "&:hover": {
                                background: "rgba(0, 0, 0, 0.07)",
                                transform: "scale(1.05)",
                            },
                            "&:active": {
                                transform: "scale(0.95)",
                                transition: "all 100ms ease",
                            },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                </Toolbar>
            </AppBar>

            {/* ── Body ─────────────────────────────────────────────────── */}
            <Box
                sx={{
                    px: 2.5,
                    py: 3,
                    flex: 1,
                    overflow: "auto",
                    WebkitOverflowScrolling: "touch",
                    minHeight: 200,
                    /* Thin custom scrollbar */
                    "&::-webkit-scrollbar": { width: 6 },
                    "&::-webkit-scrollbar-track": {
                        background: "transparent",
                    },
                    "&::-webkit-scrollbar-thumb": {
                        background: "rgba(255, 144, 21, 0.18)",
                        borderRadius: 3,
                        "&:hover": { background: "rgba(255, 144, 21, 0.30)" },
                    },
                }}
            >
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 600,
                        color: "#4b5263",
                        mb: 2,
                        display: "flex",
                        alignItems: "center",
                        letterSpacing: "0.01em",
                        animation: `${fadeSlideUp} 400ms cubic-bezier(0, 0, 0.2, 1) 180ms both`,
                    }}
                >
                    <Langs str="Associated Members" />
                    {associatedMembers.length > 0 && (
                        <Badge
                            badgeContent={associatedMembers.length}
                            sx={{
                                ml: 2,
                                "& .MuiBadge-badge": {
                                    bgcolor: ORANGE,
                                    color: "#fff",
                                    fontWeight: 700,
                                    fontSize: "0.72rem",
                                    minWidth: 22,
                                    height: 22,
                                    borderRadius: "11px",
                                    boxShadow: `0 2px 8px ${ORANGE_GLOW}`,
                                    animation: `${badgePop} 300ms cubic-bezier(0.34, 1.56, 0.64, 1) both`,
                                },
                            }}
                        />
                    )}
                </Typography>

                {options.length > 0 ? (
                    <Box
                        sx={{
                            animation: `${fadeSlideUp} 450ms cubic-bezier(0, 0, 0.2, 1) 250ms both`,
                        }}
                    >
                        <Autocomplete
                            multiple
                            value={associatedMembers}
                            onChange={(event, newValue) => setAssociatedMembers(newValue)}
                            options={options}
                            getOptionLabel={(option) => option.fullName}
                            filterOptions={(opts, { inputValue }) => {
                                const lower = inputValue.toLowerCase();
                                return opts.filter((opt) =>
                                    opt.compoundName.toLowerCase().includes(lower)
                                );
                            }}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            disableCloseOnSelect
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip
                                        {...getTagProps({ index })}
                                        key={option.id}
                                        label={option.fullName}
                                        size="small"
                                        sx={{
                                            background: `linear-gradient(135deg, rgba(255, 144, 21, 0.10) 0%, rgba(255, 193, 0, 0.08) 100%)`,
                                            color: "#b36300",
                                            fontWeight: 600,
                                            fontSize: "0.8rem",
                                            borderRadius: "10px",
                                            border: "1px solid rgba(255, 144, 21, 0.15)",
                                            height: 30,
                                            transition: "all 250ms cubic-bezier(0.4, 0, 0.2, 1)",
                                            animation: `${fadeIn} 200ms ease both`,
                                            "&:hover": {
                                                background: `linear-gradient(135deg, rgba(255, 144, 21, 0.18) 0%, rgba(255, 193, 0, 0.14) 100%)`,
                                                boxShadow: `0 2px 8px rgba(255, 144, 21, 0.12)`,
                                            },
                                            "& .MuiChip-deleteIcon": {
                                                color: "rgba(179, 99, 0, 0.5)",
                                                transition: "all 200ms ease",
                                                "&:hover": {
                                                    color: ORANGE,
                                                    transform: "scale(1.15)",
                                                },
                                            },
                                        }}
                                    />
                                ))
                            }
                            renderOption={(props, option, { selected }) => (
                                <Box
                                    component="li"
                                    {...props}
                                    key={option.id}
                                    sx={{
                                        minHeight: 52,
                                        px: 2,
                                        py: 1.25,
                                        borderRadius: "10px",
                                        mx: 0.5,
                                        my: 0.25,
                                        bgcolor: selected
                                            ? "rgba(255, 144, 21, 0.08)"
                                            : "transparent",
                                        borderLeft: selected
                                            ? `3px solid ${ORANGE}`
                                            : "3px solid transparent",
                                        transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
                                        "&:hover": {
                                            bgcolor: selected
                                                ? "rgba(255, 144, 21, 0.12)"
                                                : "rgba(0, 0, 0, 0.03)",
                                        },
                                        "&:active": {
                                            transform: "scale(0.97)",
                                            transition: "all 100ms ease",
                                        },
                                    }}
                                >
                                    <Box>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: selected ? 600 : 500,
                                                color: selected ? "#7a4200" : "#1a1d24",
                                                transition: "all 200ms ease",
                                                letterSpacing: "0.005em",
                                            }}
                                        >
                                            {option.fullName}
                                        </Typography>
                                        {option.teamName && (
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: selected
                                                        ? "rgba(255, 144, 21, 0.7)"
                                                        : "#8b92a0",
                                                    fontWeight: 400,
                                                    fontSize: "0.75rem",
                                                    transition: "all 200ms ease",
                                                }}
                                            >
                                                {option.teamName}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            )}
                            componentsProps={{
                                popper: {
                                    sx: { zIndex: 1600 },
                                },
                                paper: {
                                    sx: {
                                        borderRadius: "14px",
                                        mt: 0.5,
                                        boxShadow: [
                                            "0 4px 12px rgba(255, 144, 21, 0.06)",
                                            "0 12px 32px rgba(0, 0, 0, 0.08)",
                                        ].join(", "),
                                        border: "1px solid rgba(255, 144, 21, 0.08)",
                                        backdropFilter: "blur(16px)",
                                        WebkitBackdropFilter: "blur(16px)",
                                        background: "rgba(255, 255, 255, 0.96)",
                                        overflow: "hidden",
                                        "&::before": {
                                            content: '""',
                                            position: "absolute",
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            height: "2px",
                                            background: `linear-gradient(90deg, transparent, ${ORANGE}, transparent)`,
                                            opacity: 0.4,
                                        },
                                    },
                                },
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Recherche par nom ou nom d'équipe..."
                                    variant="outlined"
                                    fullWidth
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <>
                                                <PersonSearchIcon
                                                    sx={{
                                                        color: "#8b92a0",
                                                        fontSize: 20,
                                                        mr: 0.5,
                                                        transition: "all 250ms ease",
                                                    }}
                                                />
                                                {params.InputProps.startAdornment}
                                            </>
                                        ),
                                    }}
                                    sx={{
                                        "& input": {
                                            fontSize: "16px",
                                        },
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "14px",
                                            minHeight: 50,
                                            background: "rgba(255, 144, 21, 0.03)",
                                            transition: "all 280ms cubic-bezier(0.4, 0, 0.2, 1)",
                                            "& fieldset": {
                                                borderColor: "rgba(255, 144, 21, 0.12)",
                                                borderWidth: "1.5px",
                                                transition: "all 280ms cubic-bezier(0.4, 0, 0.2, 1)",
                                            },
                                            "&:hover fieldset": {
                                                borderColor: "rgba(255, 144, 21, 0.25)",
                                            },
                                            "&.Mui-focused": {
                                                background: "rgba(255, 144, 21, 0.02)",
                                                boxShadow: `0 0 0 3px rgba(255, 144, 21, 0.12)`,
                                                "& fieldset": {
                                                    borderColor: ORANGE,
                                                    borderWidth: "1.5px",
                                                },
                                                "& .MuiSvgIcon-root:first-of-type": {
                                                    color: ORANGE,
                                                },
                                            },
                                        },
                                        "& input::placeholder": {
                                            opacity: 0.45,
                                            fontSize: "0.9rem",
                                        },
                                    }}
                                />
                            )}
                        />
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            py: 7,
                            color: "#8b92a0",
                            animation: `${emptyBounce} 600ms cubic-bezier(0.34, 1.56, 0.64, 1) 200ms both`,
                        }}
                    >
                        <Box
                            sx={{
                                width: 72,
                                height: 72,
                                borderRadius: "20px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "rgba(255, 144, 21, 0.06)",
                                mb: 2,
                            }}
                        >
                            <GroupAddIcon
                                sx={{
                                    fontSize: 36,
                                    color: "rgba(255, 144, 21, 0.35)",
                                }}
                            />
                        </Box>
                        <Typography
                            variant="body2"
                            sx={{
                                textAlign: "center",
                                fontWeight: 500,
                                color: "#8b92a0",
                                letterSpacing: "0.01em",
                            }}
                        >
                            {mode ? (
                                "Aucun manager disponible à ajouter"
                            ) : (
                                "Aucun membre disponible à ajouter"
                            )}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* ── Sticky Bottom Button ─────────────────────────────────── */}
            <Box
                sx={{
                    position: "sticky",
                    bottom: 0,
                    px: 2.5,
                    py: 2.5,
                    bgcolor: "rgba(255, 255, 255, 0.88)",
                    backdropFilter: "blur(16px) saturate(1.6)",
                    WebkitBackdropFilter: "blur(16px) saturate(1.6)",
                    borderTop: "1px solid rgba(255, 144, 21, 0.08)",
                    animation: `${fadeSlideUp} 400ms cubic-bezier(0, 0, 0.2, 1) 320ms both`,
                }}
            >
                <Button
                    fullWidth
                    variant="contained"
                    disableElevation
                    onClick={handleAdd}
                    disabled={associatedMembers.length === 0}
                    sx={{
                        background: `linear-gradient(135deg, ${ORANGE} 0%, #FFA940 100%)`,
                        color: "#fff",
                        borderRadius: "14px",
                        minHeight: 52,
                        fontWeight: 650,
                        textTransform: "none",
                        fontSize: "1rem",
                        letterSpacing: "0.01em",
                        position: "relative",
                        overflow: "hidden",
                        boxShadow: `0 4px 16px rgba(255, 144, 21, 0.30)`,
                        transition: "all 280ms cubic-bezier(0.4, 0, 0.2, 1)",
                        /* Glossy inner highlight */
                        "&::before": {
                            content: '""',
                            position: "absolute",
                            inset: 0,
                            background:
                                "linear-gradient(135deg, rgba(255,255,255,0.20) 0%, transparent 50%)",
                            borderRadius: "inherit",
                            pointerEvents: "none",
                        },
                        "&:hover": {
                            background: `linear-gradient(135deg, ${ORANGE_DARK} 0%, ${ORANGE} 100%)`,
                            boxShadow: `0 6px 24px rgba(255, 144, 21, 0.40)`,
                            transform: "translateY(-1px)",
                        },
                        "&:active": {
                            transform: "scale(0.98) translateY(0px)",
                            boxShadow: `0 2px 8px rgba(255, 144, 21, 0.25)`,
                            transition: "all 100ms ease",
                        },
                        "&.Mui-disabled": {
                            background: "rgba(255, 144, 21, 0.18)",
                            color: "rgba(255, 255, 255, 0.7)",
                            boxShadow: "none",
                        },
                    }}
                >
                    <Langs str="ADD" />
                    {associatedMembers.length > 0 && (
                        <Typography
                            component="span"
                            sx={{
                                ml: 1,
                                fontWeight: 700,
                                fontSize: "1rem",
                                animation: `${badgePop} 250ms cubic-bezier(0.34, 1.56, 0.64, 1) both`,
                            }}
                        >
                            ({associatedMembers.length})
                        </Typography>
                    )}
                </Button>
            </Box>
        </Dialog>
    );
}
